import { useEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  ErrorOutline,
  ReportProblem,
  Info,
  CheckCircleOutline,
  CheckCircle,
  Cancel,
  Check,
  ContentCopy,
  Verified,
  GppMaybe,
} from "@mui/icons-material";
import { V2_COLORS } from "../constants/colors";
import { getFieldLabel } from "../constants/fieldLabels";
import { convertToINR, convertDate } from "@/helpers/Utils";
import {
  hasEInvoiceMark,
  hasEInvoiceVerdict,
  isAuthenticated,
  isJudgeable,
} from "../utils/documentAuthentication";
import {
  EXTRACTION_ISSUE_SEVERITY,
  PARSE_RESPONSE_STATUS,
  type AuthenticationMarkType,
  type DocumentAuthentication,
  type ExtractedInvoiceData,
  type ExtractionIssue,
  type ExtractionIssueSeverity,
  type ParseResultDetail,
} from "../types/invoice";

// --- Severity styling ---

const SEVERITY_STYLES: Record<
  ExtractionIssueSeverity,
  { label: string; color: string; bg: string; icon: React.ElementType }
> = {
  [EXTRACTION_ISSUE_SEVERITY.MISSING_REQUIRED]: {
    label: "Missing",
    color: V2_COLORS.error,
    bg: V2_COLORS.errorBg,
    icon: ErrorOutline,
  },
  [EXTRACTION_ISSUE_SEVERITY.AMBIGUOUS]: {
    label: "Ambiguous",
    color: V2_COLORS.warning,
    bg: V2_COLORS.warningBg,
    icon: ReportProblem,
  },
  [EXTRACTION_ISSUE_SEVERITY.INCONSISTENT]: {
    label: "Inconsistent",
    color: V2_COLORS.warning,
    bg: V2_COLORS.warningBg,
    icon: ReportProblem,
  },
};

// OCR-extracted dates are unreliable — fall back to the raw string when
// new Date() can't parse it, so we never render "NaN-NaN-NaN".
function formatExtractedDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? raw : convertDate(raw);
}

// --- Rendering helpers ---

function ExtractedField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={0.75}>
      <Typography
        fontSize="12px"
        fontWeight={600}
        color={V2_COLORS.textSecondary}
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography fontSize="13px" sx={{ wordBreak: "break-word" }}>
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

const MARK_TYPE_LABELS: Record<AuthenticationMarkType, string> = {
  manual: "Manual",
  digital: "Digital",
};

/**
 * Seal / signature verdict: green tick + type when present, red cross when
 * absent. A non-boolean presence flag (block missing on pre-v12 parses, or a
 * partial block) is "no verdict" and renders the list's neutral em dash.
 */
function AuthenticationMark({
  present,
  type,
}: {
  present: boolean | null | undefined;
  type: AuthenticationMarkType | null | undefined;
}) {
  if (typeof present !== "boolean") return <>—</>;
  const Icon = present ? CheckCircle : Cancel;
  const typeLabel = type ? MARK_TYPE_LABELS[type] : null;
  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.75}>
      <Icon
        sx={{ fontSize: 18, color: present ? V2_COLORS.success : V2_COLORS.error, flexShrink: 0 }}
        titleAccess={present ? "Present" : "Absent"}
      />
      {present &&
        (typeLabel ? (
          <Typography component="span" fontSize="13px">
            {typeLabel}
          </Typography>
        ) : (
          <Typography component="span" fontSize="13px" color={V2_COLORS.textSecondary}>
            Type undetermined
          </Typography>
        ))}
    </Box>
  );
}

type CopyState = "idle" | "copied" | "failed";

const COPY_TITLES: Record<CopyState, string> = {
  idle: "Copy IRN",
  copied: "Copied",
  failed: "Copy failed — select the value instead",
};

/** Middle-truncates the IRN and copies the full value on click. */
function CopyableHash({ value }: { value: string }) {
  const [state, setState] = useState<CopyState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const short = value.length > 20 ? `${value.slice(0, 8)}…${value.slice(-8)}` : value;

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const flash = (next: CopyState) => {
    setState(next);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), 1500);
  };

  // Never report a success we did not achieve: the clipboard is absent outside a
  // secure context and the write can be refused (document not focused). The
  // tooltip still exposes the full value for manual selection either way.
  const copy = async () => {
    if (!navigator.clipboard) {
      flash("failed");
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      flash("copied");
    } catch {
      flash("failed");
    }
  };

  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
      <Tooltip title={value}>
        <Typography component="span" fontSize="13px" sx={{ fontFamily: "monospace" }}>
          {short}
        </Typography>
      </Tooltip>
      <Tooltip title={COPY_TITLES[state]}>
        <IconButton size="small" onClick={copy} aria-label="Copy IRN" sx={{ p: 0.25 }}>
          {state === "copied" ? (
            <Check sx={{ fontSize: 14, color: V2_COLORS.success }} />
          ) : (
            <ContentCopy
              sx={{
                fontSize: 14,
                color: state === "failed" ? V2_COLORS.error : V2_COLORS.textSecondary,
              }}
            />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * E-invoice verdict: a tick plus the IRN when the IRP credentials are complete
 * (IRN + QR + a well-formed hash), a cross when the parser reported on them and
 * they are not, and the neutral em dash when it never reported (pre-v15 parse).
 */
function EInvoiceMark({ docAuth }: { docAuth: DocumentAuthentication | null }) {
  if (!hasEInvoiceVerdict(docAuth)) return <>—</>;
  const valid = hasEInvoiceMark(docAuth);
  const Icon = valid ? CheckCircle : Cancel;
  const irn = (docAuth?.irn_number ?? "").trim();
  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.75} flexWrap="wrap">
      <Icon
        sx={{ fontSize: 18, color: valid ? V2_COLORS.success : V2_COLORS.error, flexShrink: 0 }}
        titleAccess={valid ? "Present" : "Absent"}
      />
      {valid ? <CopyableHash value={irn} /> : null}
    </Box>
  );
}

/**
 * Overall verdict, mirroring the backend gate (VIP ADR 0045): any one of seal,
 * signature or a complete e-invoice mark authenticates the document. Hidden
 * when the block carries no verdict, so it never contradicts the em-dash rows.
 */
function AuthenticationVerdict({ docAuth }: { docAuth: DocumentAuthentication | null }) {
  if (!isJudgeable(docAuth)) return null;
  const ok = isAuthenticated(docAuth);
  const Icon = ok ? Verified : GppMaybe;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={ok ? "Authenticated" : "Not authenticated"}
      sx={{
        height: 20,
        fontSize: "11px",
        fontWeight: 600,
        color: ok ? V2_COLORS.success : V2_COLORS.error,
        backgroundColor: ok ? V2_COLORS.successBg : V2_COLORS.errorBg,
        "& .MuiChip-icon": { color: "inherit", ml: 0.5 },
      }}
    />
  );
}

function IssueRow({ issue }: { issue: ExtractionIssue }) {
  const style = SEVERITY_STYLES[issue.severity] ?? {
    label: issue.severity,
    color: V2_COLORS.textSecondary,
    bg: V2_COLORS.bgMuted,
    icon: Info,
  };
  const Icon = style.icon;

  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} py={1}>
      <Icon sx={{ fontSize: 18, color: style.color, flexShrink: 0, mt: "2px" }} />
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} mb={0.25}>
          <Typography fontSize="13px" fontWeight={600}>
            {getFieldLabel(issue.field)}
          </Typography>
          <Box
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              color: style.color,
              backgroundColor: style.bg,
              borderRadius: "4px",
              px: 0.75,
              py: "1px",
            }}
          >
            {style.label}
          </Box>
        </Box>
        <Typography fontSize="12px" color={V2_COLORS.textSecondary}>
          {issue.reason}
        </Typography>
      </Box>
    </Box>
  );
}

// --- Main component ---

interface ExtractionResultCardProps {
  parseResponse: ParseResultDetail | null | undefined;
}

export default function ExtractionResultCard({ parseResponse }: ExtractionResultCardProps) {
  if (!parseResponse) {
    return (
      <Box>
        <Typography variant="h4" mb={1.5}>
          Extraction Result
        </Typography>
        <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", py: 3, px: 2 }}>
          <Typography color="textSecondary" textAlign="center">
            No extraction data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const data: ExtractedInvoiceData | null = parseResponse.response;
  const issues: ExtractionIssue[] = data?.extraction_issues ?? [];
  const docAuth = data?.document_authentication ?? null;
  const topLevelError = parseResponse.error ?? data?.error ?? null;
  const status = parseResponse.status;
  const failed = status === PARSE_RESPONSE_STATUS.FAILED;
  const partial = status === PARSE_RESPONSE_STATUS.PARTIAL;
  const success = status === PARSE_RESPONSE_STATUS.SUCCESS;

  const bannerFg = failed ? V2_COLORS.error : success ? V2_COLORS.success : V2_COLORS.warning;
  const bannerBg = failed
    ? V2_COLORS.errorBg
    : success
    ? V2_COLORS.successBg
    : V2_COLORS.warningBg;
  const BannerIcon = success ? CheckCircleOutline : ErrorOutline;

  return (
    <Box>
      <Typography variant="h4" mb={1.5}>
        Extraction Result
      </Typography>
      <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", py: 2, px: 2 }}>
        {/* Status banner */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          px={1.5}
          py={1}
          mb={2}
          sx={{
            bgcolor: bannerBg,
            borderRadius: "6px",
          }}
        >
          <BannerIcon sx={{ fontSize: 18, color: bannerFg }} />
          <Typography fontSize="13px" fontWeight={600} color={bannerFg}>
            {failed
              ? "OCR extraction failed. Validation could not run."
              : partial
              ? `Extraction completed with ${issues.length} issue${issues.length === 1 ? "" : "s"}.`
              : "Extraction completed."}
          </Typography>
        </Box>

        {/* Top-level error (engine error, not field-level) */}
        {topLevelError && (
          <Box
            sx={{
              bgcolor: V2_COLORS.errorBg,
              border: `1px solid ${V2_COLORS.error}`,
              borderRadius: "6px",
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography fontSize="12px" fontWeight={600} color={V2_COLORS.error} mb={0.5}>
              Extraction error
            </Typography>
            <Typography fontSize="12px" color={V2_COLORS.error} sx={{ wordBreak: "break-word" }}>
              {topLevelError}
            </Typography>
          </Box>
        )}

        {/* Extracted fields */}
        {data ? (
          <Box mb={issues.length > 0 ? 2 : 0}>
            <Typography fontSize="13px" fontWeight={600} mb={1}>
              Extracted data
            </Typography>
            <Box>
              <ExtractedField label="Invoice Number" value={data.invoice_number} />
              <ExtractedField
                label="Invoice Date"
                value={formatExtractedDate(data.invoice_date)}
              />
              <ExtractedField label="PO Number" value={data.po_number} />
              <ExtractedField
                label="Invoice Value"
                value={
                  data.invoice_total_value != null ? convertToINR(data.invoice_total_value) : null
                }
              />
              <ExtractedField label="Billing Entity" value={data.bill_to_entity} />
              <ExtractedField label="Bill to GSTIN" value={data.bill_to_gstin} />
              <ExtractedField label="Bill to Address" value={data.bill_to_address} />
              <ExtractedField label="Ship to Entity" value={data.ship_to_entity} />
              <ExtractedField label="Ship to GSTIN" value={data.ship_to_gstin} />
              <ExtractedField label="Ship to Address" value={data.ship_to_address} />
              <ExtractedField label="Seller Entity" value={data.seller_entity} />
              <ExtractedField label="Seller GSTIN" value={data.seller_gstin} />
              <ExtractedField label="Seller Address" value={data.seller_address} />
            </Box>
            <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
              <Typography fontSize="13px" fontWeight={600}>
                Document authentication
              </Typography>
              <AuthenticationVerdict docAuth={docAuth} />
            </Box>
            <Box>
              <ExtractedField
                label="Seal"
                value={<AuthenticationMark present={docAuth?.seal_present} type={docAuth?.seal_type} />}
              />
              <ExtractedField
                label="Signature"
                value={
                  <AuthenticationMark
                    present={docAuth?.signature_present}
                    type={docAuth?.signature_type}
                  />
                }
              />
              <ExtractedField label="E-invoice (IRN)" value={<EInvoiceMark docAuth={docAuth} />} />
            </Box>
          </Box>
        ) : (
          !topLevelError && (
            <Typography fontSize="13px" color={V2_COLORS.textSecondary} sx={{ py: 2 }}>
              No fields were extracted.
            </Typography>
          )
        )}

        {/* Extraction issues */}
        {issues.length > 0 && (
          <Box>
            <Typography fontSize="13px" fontWeight={600} mb={0.5}>
              Issues ({issues.length})
            </Typography>
            {issues.map((issue, idx) => (
              <Box
                key={`${issue.field}-${idx}`}
                sx={{
                  borderBottom:
                    idx < issues.length - 1 ? `1px solid ${V2_COLORS.border}` : "none",
                }}
              >
                <IssueRow issue={issue} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
